import {
  HomePageWidgetConfig,
  type HomePageWidgetPosition,
} from 'pages/HomePage/constants';
import { InlineLabelInput, SkeletonPage } from 'components/common';
import { Panel, PickerInput, Text, Button, FlexRow } from '@epam/promo';
import { useArrayDataSource } from '@epam/uui-core';
import css from './PersonalizationPage.module.scss';
import { useState } from 'react';
import {
  useGetHomePageWidgetsQuery,
  useSaveHomePageWidgetsQuery,
  useResetHomePageWidgetsQuery,
} from 'hooks/api/appSettings';
import { WidgetDefinition } from 'api/appSettings.service';
import { useModal } from 'hooks/ui';

const emptyConfig: HomePageWidgetConfig = {
  cell_0_0: '',
  cell_0_1: '',
  cell_1_0: '',
  cell_1_1: '',
};

export default function HomePagePersonalization() {
  const { personalizedConfig, isCustomized, isLoading, widgets } =
    useGetHomePageWidgetsQuery();
  const { mutate: savePersonalizedConfig, isLoading: isSaving } =
    useSaveHomePageWidgetsQuery();
  const { mutate: resetPersonalizedConfig, isLoading: isResetting } =
    useResetHomePageWidgetsQuery();
  const modal = useModal();

  const [draftFormData, setDraftFormData] =
    useState<HomePageWidgetConfig | null>(null);
  const formData = draftFormData ?? personalizedConfig ?? emptyConfig;
  const isBusy = isSaving || isResetting;

  const handleChange = (
    position: HomePageWidgetPosition,
    newValue?: string,
  ) => {
    setDraftFormData((prev) => {
      const updatedConfig = {
        ...(prev || personalizedConfig || emptyConfig),
        [position]: newValue,
      };

      return updatedConfig;
    });
  };

  const handleSubmit = () => {
    savePersonalizedConfig(formData, {
      onSuccess: () => {
        setDraftFormData(null);
      },
    });
  };

  const handleReset = () => {
    modal.confirm({
      text: 'Are you sure you want to reset your personalization settings to the default ones? This cannot be undone.',
      successAction: 'Yes',
      onSuccess: () => {
        resetPersonalizedConfig(undefined, {
          onSuccess: () => {
            setDraftFormData(null);
          },
        });
      },
    });
  };

  const dataSource = useArrayDataSource<
    WidgetDefinition,
    string | undefined,
    unknown
  >(
    {
      items: widgets ?? [],
    },
    [widgets],
  );

  // ========================= Render =========================

  const renderSelect = (
    formData: HomePageWidgetConfig,
    position: HomePageWidgetPosition,
    label: string,
  ) => (
    <InlineLabelInput label={label}>
      <PickerInput
        dataSource={dataSource}
        isDisabled={isBusy}
        selectionMode="single"
        value={formData[position] || undefined}
        onValueChange={(newValue) => {
          handleChange(position, newValue);
        }}
      />
    </InlineLabelInput>
  );

  return isLoading ? (
    <SkeletonPage />
  ) : (
    <>
      <Panel
        cx={{
          [css.form]: true,
        }}>
        <Text
          color="gray90"
          fontSize="24"
          fontWeight="600"
          lineHeight="24"
          rawProps={{ style: { marginBottom: 24 } }}>
          Home Page Widgets
        </Text>

        {renderSelect(formData, 'cell_0_0', 'Top Left')}
        {renderSelect(formData, 'cell_0_1', 'Top Right')}
        {renderSelect(formData, 'cell_1_0', 'Bottom Left')}
        {renderSelect(formData, 'cell_1_1', 'Bottom Right')}

        <FlexRow cx={css.form__actions}>
          <Button
            caption={isSaving ? 'Saving...' : 'Save Layout'}
            color="blue"
            cx={css.form__button}
            isDisabled={isBusy}
            onClick={handleSubmit}
          />

          <Button
            caption={isResetting ? 'Resetting...' : 'Reset to default settings'}
            color="red"
            cx={css.form__button}
            fill="white"
            isDisabled={isBusy || !isCustomized}
            onClick={handleReset}
          />
        </FlexRow>
      </Panel>
    </>
  );
}
